/**
 * The studio header piece: the site's fixed header on every page. The studio's
 * mark (its logo beside its name, or the name alone as the wordmark), a link to
 * each thing the studio has, and Sign in with Telegram.
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'studio-header',
  description:
    "The site's fixed header: the studio's mark going home, a link to each section or page the studio has, and Sign in with Telegram, or once signed in the Messages button with its unread count and the client's name opening a menu with Bookings, Messages, Profile and Sign out.",
  options: {
    showNav: { type: 'boolean', default: true, description: 'Show the links to the studio\'s sections and pages.', showsPart: 'nav' },
    showSignIn: { type: 'boolean', default: true, description: 'Show Sign in with Telegram.', showsPart: 'signIn' },
  },
  parts: {
    header: { description: 'The whole header bar.', required: false },
    mark: { description: "The studio's mark; opens the home page.", required: false },
    logo: { description: "The studio's logo image, when it has one.", required: false },
    name: { description: "The studio's name, or its wordmark when it has no logo.", required: false },
    nav: { description: 'The links to the studio\'s sections and pages.', required: false },
    link: { description: 'One link: Services, Events, Open times or About.', required: false, states: ['current'] },
    signIn: { description: 'The Sign in with Telegram button.', required: false },
    messages: { description: "Signed in: the Messages button before the client's initials, opening and closing the messages panel.", required: false },
    messagesBadge: { description: "The studio's unread messages on the Messages button; hidden when there are none.", required: false },
    account: { description: "Signed in: the client's initials and name, opening the account menu.", required: false, states: ['open'] },
    accountMenu: { description: "The account menu under the client's name.", required: false },
    menuBookings: { description: 'Bookings, in the account menu.', required: false },
    menuMessages: { description: 'Messages with the unread count, in the account menu.', required: false },
    menuProfile: { description: 'Profile, in the account menu.', required: false },
    signOut: { description: 'Sign out, in the account menu.', required: false },
    signOutError: { description: 'Under Sign out when it did not go through: the client is still signed in.', required: false },
  },
  actions: {
    goHome: { description: 'Opens the home page at its top.', args: [] },
    open: { description: 'Opens one of the header links.', args: [{ name: 'linkId', kind: 'item', list: 'links', key: 'id' }] },
    signIn: { description: "Opens Telegram's sign-in window; the visitor stays on the page they were on.", args: [] },
    toggleMenu: { description: 'Opens or closes the account menu.', args: [{ name: 'open', kind: 'value', type: 'boolean', optional: true }] },
    toggleMessages: { description: 'Opens the messages panel, or closes it when open.', args: [] },
    openAccount: { description: "Opens the client's Bookings or Profile page.", args: [{ name: 'page', kind: 'value', type: 'string', values: ['bookings', 'profile'] }] },
    signOut: { description: 'Signs out on this website; the page stays where it is.', args: [] },
  },
  events: {
    navigated: 'A visitor followed a header link or the mark.',
    signInPressed: 'A visitor pressed Sign in with Telegram.',
    signedOut: 'The client signed out.',
    messagesToggled: 'The client opened or closed the messages panel from the header.',
    accountOpened: 'The client opened Bookings or Profile from the account menu.',
  },
});

/** Where a header link goes: a home section, or the events page. */
export type StudioLinkId = 'services' | 'events' | 'times' | 'about';

export interface StudioLink {
  /** Which link this is. */
  readonly id: StudioLinkId;
  /** The words drawn: Services, Events, Open times or About. */
  readonly label: string;
  /** The address it opens: `/#services`, `/events`, `/#times` or `/#about`. */
  readonly href: string;
  /** True while the visitor is on what it opens (Events on /events or an event, Services on a service). */
  readonly current: boolean;
}

/** What the studio header knows. */
export interface StudioHeaderState {
  /** loading until the studio's profile is known; error when it failed. */
  readonly status: 'loading' | 'ready' | 'error';
  /** The studio's name; empty while loading. */
  readonly name: string;
  /** The studio's logo image, or null to draw the name alone as the wordmark. */
  readonly logoUrl: string | null;
  /** The links to draw, only for what the studio has, in header order. */
  readonly links: readonly StudioLink[];
  /** Who is here: loading until known, then signed out or signed in (web-sign-in-checkout). */
  readonly session: 'loading' | 'signedOut' | 'signedIn';
  /** The signed-in client: their name and initials; null when signed out. */
  readonly client: { readonly name: string; readonly initials: string } | null;
  /** True once Sign in can open Telegram's window (its one-time value and library are ready). */
  readonly signInReady: boolean;
  /** The last sign-in attempt: idle, pending, error (it did not go through) or unavailable (the studio has no bot yet). */
  readonly signIn: 'idle' | 'pending' | 'error' | 'unavailable';
  /** True while the account menu is open. */
  readonly menuOpen: boolean;
  /** True after a Sign out the server did not take: the client is still signed in and may try again. */
  readonly signOutFailed: boolean;
  /** Signed in: the studio's secure messages the client has not read (the Mini App's chat badge); 0 when none. */
  readonly unread: number;
  /** True while the messages panel is open. */
  readonly messagesOpen: boolean;
}

/** What a drawing can ask the studio header to do. */
export interface StudioHeaderActions {
  /** Open the home page at its top. */
  readonly goHome: () => void;
  /** Open the link with this id; an id not in `links` is ignored. */
  readonly open: (linkId: StudioLinkId) => void;
  /** Sign in with Telegram, coming back to this page signed in. */
  readonly signIn: () => void;
  /** Open or close the account menu; `false` closes it. */
  readonly toggleMenu: (open?: boolean) => void;
  /** Sign out on this website. */
  readonly signOut: () => void;
  /** Open the messages panel, or close it when open; the account menu closes. */
  readonly toggleMessages: () => void;
  /** Open the client's Bookings or Profile page; the account menu closes. */
  readonly openAccount: (page: 'bookings' | 'profile') => void;
}

/** What the studio header tells its host. */
export interface StudioHeaderEvents {
  /** A visitor followed a link (`home` for the mark). */
  readonly navigated: { readonly linkId: StudioLinkId | 'home' };
  /** A visitor pressed Sign in with Telegram. */
  readonly signInPressed: Record<string, never>;
  /** The client signed out. */
  readonly signedOut: Record<string, never>;
  /** The messages panel opened (`open` true) or closed from the header. */
  readonly messagesToggled: { readonly open: boolean };
  /** The client opened Bookings or Profile from the account menu. */
  readonly accountOpened: { readonly page: 'bookings' | 'profile' };
}
